import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { createInvite } from '../../services/inviteService';
import { templates, defaultPattern } from './templates';
import './styles/pages.css';

const Send = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { templateId, question, cta, pattern = defaultPattern } = location.state || {};

  const [sendMethod, setSendMethod] = useState(null);
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Email state
  const [email, setEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');

  const template = templates[templateId];
  const senderName = user?.name || 'Someone special';

  if (!template || !question || !cta) {
    navigate('/Playground/Inviteapp/templates');
    return null;
  }

  // Generate the invite link - uses current origin for dev, production URL for prod
  const generateInviteLink = (shortId) => {
    const baseUrl = process.env.NODE_ENV === 'production'
      ? 'https://stray-apps.com'
      : window.location.origin;
    return `${baseUrl}/Playground/Inviteapp/invite/${shortId}`;
  };

  const handleCreateInvite = async () => {
    if (created) return;

    setLoading(true);
    try {
      const invite = await createInvite({
        templateId,
        question,
        cta,
        pattern,
        userId: user?.id || 'anonymous',
        userEmail: user?.email || 'anonymous',
        userName: senderName,
      });

      const link = generateInviteLink(invite.shortId);
      setInviteLink(link);
      setCreated(true);
    } catch (error) {
      console.error('Error creating invite:', error);
      alert('Failed to create invite. Please try again.');
    }
    setLoading(false);
  };

  const handleCopyLink = (showPopup = false) => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    if (showPopup) {
      setShowSuccessPopup(true);
    }
  };

  const handleSendWhatsApp = () => {
    const text = encodeURIComponent(
      `\uD83D\uDC8C ${senderName} sent you a special invite!\n\nClick to open: ${inviteLink}\n\n(Try clicking "No" if you dare! \uD83D\uDE0F)`
    );
    window.open(`https://wa.me/?text=${text}`);
    setShowSuccessPopup(true);
  };

  const handleSendEmail = async () => {
    if (!email) return;

    const emailApiUrl = process.env.REACT_APP_EMAIL_API_URL;

    // If no API configured, fall back to mailto
    if (!emailApiUrl) {
      const subject = encodeURIComponent(`\uD83D\uDC8C ${senderName} sent you a special invite!`);
      const body = encodeURIComponent(
        `Hey there! \uD83D\uDC95\n\n${senderName} has sent you an irresistible invite!\n\n\uD83D\uDD17 Click here to open it:\n${inviteLink}\n\n(Pro tip: Try clicking "No" if you dare! \uD83D\uDE0F)\n\n---\nSent with love via Irresistible Invites \uD83D\uDC8C\nhttps://stray-apps.com`
      );
      window.open(`mailto:${email}?subject=${subject}&body=${body}`);
      setEmailSent(true);
      setShowSuccessPopup(true);
      return;
    }

    // Send via Resend API
    setSendingEmail(true);
    setEmailError('');

    try {
      const response = await fetch(emailApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: email,
          senderName,
          inviteLink,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send email');
      }

      setEmailSent(true);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error('Error sending email:', error);
      setEmailError(error.message || 'Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="send-page">
      <motion.div
        className="send-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1>Send Your Invite {'\uD83D\uDC8C'}</h1>

        {!created ? (
          <motion.div className="create-link-section">
            <p>Generate a unique link for your invite!</p>
            <motion.button
              className="generate-button"
              onClick={handleCreateInvite}
              disabled={loading}
              style={{
                background: `linear-gradient(135deg, ${template.colors.primary}, ${template.colors.secondary})`,
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? '\u2728 Creating...' : '\u2728 Generate Link'}
            </motion.button>
          </motion.div>
        ) : (
          <>
            <div className="link-section">
              <p className="success-message">{'\uD83C\uDF89'} Your invite is ready!</p>
              <div className="link-box">
                <input type="text" value={inviteLink} readOnly />
                <button onClick={() => handleCopyLink()} className="copy-button">
                  {copied ? '\u2713 Copied!' : '\uD83D\uDCCB Copy'}
                </button>
              </div>
            </div>

            <div className="send-methods">
              <h3>How would you like to send it?</h3>

              <div className="method-cards">
                <motion.div
                  className={`method-card ${sendMethod === 'email' ? 'selected' : ''}`}
                  onClick={() => setSendMethod('email')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="method-icon">{'\uD83D\uDCE7'}</span>
                  <h4>Email</h4>
                  <p>Send via email</p>
                </motion.div>
                <motion.div
                  className={`method-card ${sendMethod === 'whatsapp' ? 'selected' : ''}`}
                  onClick={() => setSendMethod('whatsapp')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="method-icon">{'\uD83D\uDCAC'}</span>
                  <h4>WhatsApp</h4>
                  <p>Share via WhatsApp</p>
                </motion.div>
                <motion.div
                  className={`method-card ${sendMethod === 'link' ? 'selected' : ''}`}
                  onClick={() => setSendMethod('link')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="method-icon">{'\uD83D\uDD17'}</span>
                  <h4>Copy Link</h4>
                  <p>Share anywhere</p>
                </motion.div>
              </div>

              {sendMethod === 'email' && (
                <motion.div
                  className="email-form"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                >
                  {emailSent ? (
                    <div className="email-sent-message">
                      <p>{'\u2705'} Email sent successfully!</p>
                      <p>Your invite is on its way.</p>
                    </div>
                  ) : (
                    <>
                      {emailError && <p className="email-error">{emailError}</p>}
                      <input
                        type="email"
                        placeholder="Enter recipient's email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="email-input"
                      />
                      <button
                        onClick={handleSendEmail}
                        disabled={!email || sendingEmail}
                        className="send-action-button"
                        style={{
                          background: `linear-gradient(135deg, ${template.colors.primary}, ${template.colors.secondary})`,
                        }}
                      >
                        {sendingEmail ? 'Sending...' : `Send Email \uD83D\uDCE7`}
                      </button>
                      <p className="email-note">
                        We'll send a beautiful email with your invite link
                      </p>
                    </>
                  )}
                </motion.div>
              )}

              {sendMethod === 'whatsapp' && (
                <motion.div
                  className="whatsapp-section"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                >
                  <button
                    onClick={handleSendWhatsApp}
                    className="send-action-button whatsapp-button"
                  >
                    Open WhatsApp {'\uD83D\uDCAC'}
                  </button>
                </motion.div>
              )}

              {sendMethod === 'link' && (
                <motion.div
                  className="link-copy-section"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                >
                  <p className="link-instructions">
                    Your link is ready! Copy it and share it anywhere - text, DM, social media, you name it!
                  </p>
                  <button
                    onClick={() => handleCopyLink(true)}
                    className="send-action-button"
                    style={{
                      background: `linear-gradient(135deg, ${template.colors.primary}, ${template.colors.secondary})`,
                    }}
                  >
                    {copied ? '\u2713 Copied to Clipboard!' : '\uD83D\uDCCB Copy Link'}
                  </button>
                </motion.div>
              )}
            </div>
          </>
        )}

        <div className="send-footer">
          <button className="back-link" onClick={() => navigate('/Playground/Inviteapp/templates')}>
            {'\u2190'} Create Another Invite
          </button>
        </div>
      </motion.div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <motion.div
          className="success-popup-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowSuccessPopup(false)}
        >
          <motion.div
            className="success-popup"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', damping: 15 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="success-popup-icon">{'\uD83C\uDF89'}</div>
            <h2>Great, your irresistible invite is sent!</h2>
            <p>Fingers crossed they say Yes! {'\uD83D\uDC95'}</p>
            <motion.button
              className="coffee-button"
              onClick={() => navigate('/Playground/Inviteapp/templates')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Create Another Invite {'\uD83D\uDC8C'}
            </motion.button>
            <button
              className="maybe-later-link"
              onClick={() => navigate('/Playground')}
            >
              Back to Playground
            </button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Send;
